import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import api from "../services/httpClient";
import PaginationComponent from "../components/PaginationComponent";
import MapIcon from "../assets/images/live_tracking_rgb_red.svg";
import "../assets/styles/maps.scss";
import MapsFilters from "../components/Maps/MapsFilters";
import MapsButtons from "../components/Maps/MapsButtons";
import MapsTable from "../components/Maps/MapsTable";
import MapsLayersList from "../components/Maps/MapsLayersList";
import InformationLayerMap from "../components/InformationLayerMap";

const parseSearchArea = (searchArea) => {
  if (!searchArea) return null;
  if (typeof searchArea !== "string") return searchArea;

  try {
    return JSON.parse(searchArea.replace(/\\/g, ""));
  } catch {
    return null;
  }
};

const Maps = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  const [fetchVersion, setFetchVersion] = useState(0);

  const [addresses, setAddresses] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);
  const lastFetchedVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;

  React.useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current;
    const sort = sortConfigRef.current;

    (async () => {
      setIsLoading(true);
      try {
        const effectiveSortConfig =
          sort && sort.key ? sort : { key: "Postcode", direction: "asc" };

        const { searchArea, ...filterFields } = filtersToApply;

        const requestBody = {
          filter: filterFields,
          pagination: {
            pageNumber,
            pageSize,
            sortBy: effectiveSortConfig.key,
            sortDirection: effectiveSortConfig.direction,
          },
        };

        const parsedArea = parseSearchArea(searchArea);
        if (parsedArea) {
          requestBody.searchArea = parsedArea;
        }

        const response = await api.post(`/Address/ListByGeometry`, requestBody, {
          signal: controller.signal,
        });

        if (cancelled) return;

        const responsePageNumber = Number(response.data.pageNumber || pageNumber);
        const responseTotalCountRaw =
          response.data.totalCount ??
          response.data.itemsCount ??
          response.data.totalRecords;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setAddresses({
          items: Array.isArray(response.data.items) ? response.data.items : [],
          itemsCount: responseTotalCount,
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled || error?.name === "CanceledError" || error?.name === "AbortError") return;

        setAddresses({
          items: [],
          itemsCount: null,
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize,
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingFilters = {};

    params.forEach((value, key) => {
      incomingFilters[key] = value;
    });

    if (Object.keys(incomingFilters).length > 0) {
      setFilters(incomingFilters);
      filtersRef.current = incomingFilters;
      setAppliedFilters(incomingFilters);
      setCurrentPage(1);
      setRememberedTotalCount(null);
    }

    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, [location.search]);

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => {
      const nextFilters = {
        ...prevFilters,
        [field]: value,
      };
      filtersRef.current = nextFilters;
      return nextFilters;
    });
  };

  const handleSearch = () => {
    const nextApplied = {
      ...filtersRef.current,
      searchArea: selectedLayer?.area ?? null,
    };
    setAppliedFilters(nextApplied);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handlePageChange = (page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleLayerSelect = (layer) => {
    setSelectedLayer(layer);

    const nextApplied = {
      ...appliedFiltersRef.current,
      searchArea: layer?.area ?? null,
    };

    setAppliedFilters(nextApplied);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  return (
    <div className="maps-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col>
            <div className="d-flex align-items-center">
              <img
                src={MapIcon}
                alt="Map Pin"
                style={{ width: "30px", height: "30px", marginRight: "12px" }}
              />
              <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", margin: 0 }}>
                Mapa
              </h3>
            </div>
          </Col>
          <Col xs="auto">
            <MapsButtons
              onSearch={handleSearch}
              filters={filters}
              searchArea={selectedLayer?.area}
            />
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <MapsFilters
              onFilterChange={handleFilterChange}
              filters={filters}
              onSearch={handleSearch}
            />
          </Col>
          <Col className="flex-grow-1" />
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <Row className="mb-4">
              <MapsLayersList onLayerSelect={handleLayerSelect} />
            </Row>
            <Row>
              <MapsTable
                data={addresses.items}
                isLoading={isLoading}
                onSortChange={handleSortChange}
              />
            </Row>
            <Row className="align-items-center mt-4">
              <PaginationComponent
                itemsPerPage={addresses.pageSize}
                dataCount={addresses.items.length}
                itemsCount={addresses.itemsCount ?? rememberedTotalCount}
                totalCount={rememberedTotalCount}
                pageNumber={addresses.pageNumber}
                totalPages={addresses.totalPages}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[100, 500, 1000, 5000, 10000]}
              />
            </Row>
          </Col>
          <Col xs={12} md={8}>
            <InformationLayerMap
              pins={(() => {
                const filteredPins = addresses.items
                  .filter((item) => {
                    const hasCoords = item.coordX && item.coordY;
                    return hasCoords;
                  })
                  .map((item) => ({
                    x: item.coordX,
                    y: item.coordY,
                    id: item.id,
                  }));
                return filteredPins;
              })()}
              area={selectedLayer?.area}
              color={selectedLayer?.color}
              style={{ marginTop: "16px", height: "760px" }}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Maps;
